import React from "react";
import { motion } from "framer-motion";
import { Check, TrendingDown } from "lucide-react";
import { SITE } from "@/data/site";
import { useProgram } from "@/lib/programs";
import { czk } from "@/lib/price";

export default function ValueStack() {
  const { program } = useProgram();
  const items = program.items?.length ? program.items.map((i) => ({ label: i.label, price: i.price ? czk(i.price) : "" })) : SITE.valueStack.items;
  const v = { items, total: czk(program.regular_price_effective), bundle: czk(program.price),
    title: `Jednotlivě by vás to stálo ${czk(program.regular_price_effective)}.`,
    sub: `U nás dostanete úplně to samé v jednom programu — a ušetříte ${czk(program.savings)}.`,
    save: `Ušetříte ${czk(program.savings)}` };
  return (
    <section
      data-testid="value-stack"
      className="relative bg-brand-bg py-24 lg:py-32 border-t border-brand-border overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          {/* Left */}
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.3em] text-brand-accent">
              Co dostáváte za {czk(program.price)}
            </div>
            <h2 className="mt-4 font-display font-black text-4xl lg:text-6xl leading-[1.05] tracking-tight">
              {v.title.split(".")[0]}
              <span className="italic font-light">.</span>
            </h2>
            <p className="mt-6 text-brand-muted leading-relaxed max-w-md">{v.sub}</p>

            <div className="mt-10 inline-flex items-center gap-3 bg-brand-accent/10 border border-brand-accent/40 px-5 py-3">
              <TrendingDown size={18} className="text-brand-accent" />
              <span className="font-display font-bold text-brand-accent uppercase tracking-[0.15em] text-sm">
                {v.save}
              </span>
            </div>
          </div>

          {/* Right – value stack table */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7"
          >
            <div className="border border-brand-border">
              {v.items.map((it, i) => (
                <div
                  key={i}
                  data-testid={`value-row-${i}`}
                  className="flex items-center justify-between gap-4 px-6 lg:px-8 py-5 border-b border-brand-border"
                >
                  <div className="flex items-start gap-3 min-w-0">
                    <Check size={18} className="mt-0.5 shrink-0 text-brand-accent" />
                    <span className="text-white/90 text-sm lg:text-base">{it.label}</span>
                  </div>
                  <span className="text-brand-muted line-through font-display text-lg lg:text-xl shrink-0">
                    {it.price}
                  </span>
                </div>
              ))}

              <div className="flex items-center justify-between gap-4 px-6 lg:px-8 py-5 bg-brand-surface border-b border-brand-border">
                <span className="text-brand-muted uppercase tracking-[0.2em] text-xs">
                  Cena jednotlivě
                </span>
                <span className="font-display text-2xl text-brand-muted line-through">
                  {v.total}
                </span>
              </div>
              <div className="flex items-center justify-between gap-4 px-6 lg:px-8 py-6 bg-black">
                <div>
                  <div className="text-brand-accent uppercase tracking-[0.2em] text-xs">
                    Vaše cena v balíčku
                  </div>
                  <div className="mt-1 text-xs text-brand-muted">vše v jedné ceně, hotovo do 1 dne</div>
                </div>
                <span className="font-display font-black text-4xl lg:text-5xl text-brand-accent">
                  {v.bundle}
                </span>
              </div>
            </div>

            <a
              href="#reservation"
              data-testid="value-stack-cta"
              className="mt-6 inline-flex items-center gap-3 bg-brand-accent text-black font-semibold px-7 py-4 hover:bg-brand-accentHover transition-all group"
            >
              Chci ušetřit {czk(program.savings)}
              <span className="transition-transform group-hover:translate-x-1.5">→</span>
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
